import { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useQueryGetCustomerLimit } from "src/queries/customerLimits.queries";
import { QueryNames } from "src/queries/queries.constant";
import { useQueryGetDistributorLimitations } from "src/queries/distributor.queries";
import { normalizeDistributorLimitationsResponse } from "src/services/distributorService";
import { normalizeCustomerLimitsResponse } from "src/services/customerLimitsService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import {
  getDistributorLimitByGuardKey,
  isCustomerLimitReached,
  limitReachedDescriptionKeyByGuardKey,
  limitReachedTitleKeyByGuardKey,
  type CustomerLimitGuardKey,
} from "./customerLimitGuards";

interface UseCustomerLimitGuardParams {
  limitKey: CustomerLimitGuardKey;
  currentValue: number;
  customerId?: string | number;
  enabled?: boolean;
}

const customerLimitCodeByGuardKey: Record<CustomerLimitGuardKey, string> = {
  automations: "AUTOMATIONS",
  notifications: "NOTIFICATION_SETTINGS",
  users: "USERS",
  recipients: "RECIPIENTS",
  notificationsPerMonth: "INFO_NOTIFICATIONS_PER_MONTH",
};

export const useCustomerLimitGuard = ({
  limitKey,
  currentValue,
  customerId,
  enabled = true,
}: UseCustomerLimitGuardParams) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const selectedDistributor = useSelectDistributor((state) => state.selectedDistributor);
  const distributorId =
    selectedDistributor !== null && selectedDistributor !== undefined
      ? String(selectedDistributor)
      : undefined;

  const distributorLimitsQuery = useQueryGetDistributorLimitations(distributorId, {
    enabled: enabled && Boolean(distributorId) && !customerId,
    refetchOnMount: false,
  });
  const customerLimitsQuery = useQueryGetCustomerLimit(
    customerId,
    customerLimitCodeByGuardKey[limitKey],
    {
      enabled: enabled && Boolean(customerId),
    }
  );
  const refreshCustomerLimit = useCallback(
    (selectedCustomerId: string | number) =>
      queryClient.invalidateQueries({
        queryKey: [
          ...QueryNames.customerLimits,
          selectedCustomerId,
          customerLimitCodeByGuardKey[limitKey],
        ],
      }),
    [limitKey, queryClient]
  );

  const distributorLimits = useMemo(
    () => normalizeDistributorLimitationsResponse(distributorLimitsQuery.data?.data?.response),
    [distributorLimitsQuery.data?.data?.response]
  );

  const customerLimit = useMemo(
    () =>
      normalizeCustomerLimitsResponse(customerLimitsQuery.data?.data?.response).find(
        (limit) => limit.code === customerLimitCodeByGuardKey[limitKey]
      ),
    [customerLimitsQuery.data?.data?.response, limitKey]
  );
  const distributorMaxValue = getDistributorLimitByGuardKey(distributorLimits, limitKey);
  const maxValue =
    customerLimit?.customerMaxLimit ?? customerLimit?.maxLimit ?? distributorMaxValue;
  const remaining = customerLimit?.remaining;
  const effectiveCurrentValue = customerId
    ? (customerLimit?.value ?? currentValue)
    : currentValue;
  const isLimitReached =
    enabled && isCustomerLimitReached(effectiveCurrentValue, maxValue, remaining);
  const title = t(limitReachedTitleKeyByGuardKey[limitKey]);
  const description = t(limitReachedDescriptionKeyByGuardKey[limitKey]);

  return {
    isLimitReached,
    isFetching: customerId
      ? customerLimitsQuery.isFetching
      : distributorLimitsQuery.isFetching,
    maxValue,
    remaining,
    title,
    description,
    tooltip: isLimitReached ? title : "",
    refreshCustomerLimit,
  };
};

export type CustomerLimitGuardResult = ReturnType<typeof useCustomerLimitGuard>;
